import { AnimatePresence } from 'motion/react';
import { AnimatedSurface } from './components/Motion';
import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { AlertCircle, Bell, CheckCircle2, X } from 'lucide-react';

type ToastTone = 'success' | 'error' | 'info';

export interface ToastMessage {
  id: number;
  title: string;
  description?: string;
  tone: ToastTone;
}

const ToastContext = createContext<((message: Omit<ToastMessage, 'id'>) => void) | undefined>(
  undefined,
);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);
  const nextId = useRef(0);
  const dismiss = useCallback(
    (id: number) => setToasts((items) => items.filter((item) => item.id !== id)),
    [],
  );
  const notify = useCallback(
    (message: Omit<ToastMessage, 'id'>) => {
      const id = ++nextId.current;
      setToasts((items) => [...items.slice(-3), { ...message, id }]);
      window.setTimeout(() => dismiss(id), message.tone === 'error' ? 7000 : 4500);
    },
    [dismiss],
  );

  return (
    <ToastContext.Provider value={notify}>
      {children}
      <div className="toast-viewport" aria-live="polite" aria-atomic="false">
        <AnimatePresence initial={false}>{toasts.map((toast) => (
          <AnimatedSurface variant="toast" origin="bottom right"
            className={`toast toast--${toast.tone}`}
            key={toast.id}
            role={toast.tone === 'error' ? 'alert' : 'status'}
          >
            {toast.tone === 'success' ? (
              <CheckCircle2 />
            ) : toast.tone === 'error' ? (
              <AlertCircle />
            ) : (
              <Bell />
            )}
            <div>
              <strong>{toast.title}</strong>
              {toast.description && <span>{toast.description}</span>}
            </div>
            <button onClick={() => dismiss(toast.id)} aria-label="Dismiss notification">
              <X />
            </button>
          </AnimatedSurface>
        ))}</AnimatePresence>
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const notify = useContext(ToastContext);
  if (!notify) throw new Error('useToast must be used inside ToastProvider');
  return notify;
}
